import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import orgState from '@/org-app/store/state';
import { createApiAction, createGetAllAction } from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  clearReferenceData({ commit }) {
    commit(types.CLEAR_REFERENCE_DATA);
  },

  getAllCountries({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getCountries(
        (locations) => {
          resolve(locations);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCountries');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLocation({ commit }, { id, includeShapes = false }) {
    return new Promise((resolve, reject) => {
      API.instance.getLocation(
        { id, includeShapes },
        (location) => {
          resolve(location);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLocation');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLocations({ commit }, ids) {
    return new Promise((resolve, reject) => {
      API.instance.getLocations(
        ids,
        (locations) => {
          resolve(locations);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLocations');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLocationsAsync: createGetAllAction(API.instance.getLocationsAsync, null, 'messages.failedToGetLocations', false),

  async searchLocations({ commit }, { queryParams, cancelTokenSource = null, showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const locations = await API.instance.searchLocations({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetLocations');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return locations;
  },

  getLocationsByLevel: createApiAction(API.instance.getLocationsByLevel, null, 'messages.failedToGetLocations', false),

  getTags: createApiAction(API.instance.getTags, null, 'messages.failedToGetLocations', false),

  getLocationSites({ commit }, { location, networkIDs, ownerIDs }) {
    return new Promise((resolve, reject) => {
      API.instance.getLocationSites(
        location,
        networkIDs,
        ownerIDs,
        (sites) => {
          resolve(sites);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLocationSites');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getChildrenLocations({ commit }, { location, networkIDs, ownerIDs }) {
    return new Promise((resolve, reject) => {
      API.instance.getChildrenLocations(
        location,
        networkIDs,
        ownerIDs,
        (locations) => {
          commit(types.UPDATE_CHILDREN_LOCATIONS, { location, locations });
          resolve(locations);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetChildrenLocations');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  treeSearchLocations({ commit }, { searchTerm, params }) {
    return new Promise((resolve, reject) => {
      API.instance.treeSearchLocations(
        searchTerm,
        params.country_ids,
        params.network_ids,
        params.cancelTokenSource,
        (locations) => {
          commit(types.UPDATE_LOCATION_TREESEARCH_RESULTS, locations);
          resolve(locations);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToTreeSearchLocations');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getCitiesWithinRadius({ commit }, { lat, lng }) {
    return new Promise((resolve, reject) => {
      API.instance.getCitiesWithinRadius(
        lat,
        lng,
        (locations) => {
          resolve(locations);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToTreeSearchLocations');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getAllDataProviders({ commit }, params) {
    try {
      const result = await API.instance.getDataProviders(params);
      commit(types.UPDATE_DATAPROVIDERS_LIST, result);
      return result;
    } catch (error) {
      const msg = i18n.global.t('messages.failedToGetDataProviders');
      ErrorHandler.showError(msg, error);
      throw error;
    }
  },

  getAllExternalDemographics({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getExternalDemographics(
        null,
        (externalDemographics) => {
          commit(types.UPDATE_EXTERNALDEMOGRAPHICS_LIST, externalDemographics);
          resolve(externalDemographics);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetExternalDemographics');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getExternalDemographics({ commit }, queryParams) {
    return new Promise((resolve, reject) => {
      API.instance.getExternalDemographics(
        queryParams,
        (externalDemographics) => {
          commit(types.UPDATE_EXTERNALDEMOGRAPHICS_LIST, externalDemographics);
          resolve(externalDemographics);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetExternalDemographics');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLanguages: createGetAllAction(
    API.instance.getLanguages,
    types.UPDATE_LANGUAGE_LIST,
    'messages.failedToGetLanguages',
    false,
  ),

  getSupplierCountryRoots: createApiAction(
    API.instance.getSupplierCountryRoots,
    types.UPDATE_SUPPLIER_COUNTRY_ROOTS,
    'messages.failedToGetMediaOwners',
    false,
  ),

  getMediaOwners: createGetAllAction(
    API.instance.getMediaOwners,
    types.UPDATE_MEDIA_OWNERS_LIST,
    'messages.failedToGetMediaOwners',
    false,
    null,
    null,
    false,
  ),

  getCurators: createGetAllAction(
    API.instance.getCurators,
    types.UPDATE_CURATORS_LIST,
    'messages.failedToGetCurators',
    false,
    null,
    null,
    true,
  ),

  getBuyers: createGetAllAction(
    API.instance.getBuyersAsync,
    types.UPDATE_BUYERS_LIST,
    'messages.failedToGetBuyers',
    false,
  ),

  getOrganizationDSPIDs({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getOrganizationDSPIDs(
        (orgDSPIDs) => {
          commit(types.UPDATE_ORGANIZATION_DSP_IDS_LIST, orgDSPIDs);
          resolve(orgDSPIDs);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetBuyers');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getExternalOrganizationDSPIDs({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getOrganizationDSPIDs(
        (orgDSPIDs) => {
          commit(types.UPDATE_EXTERNAL_ORGANIZATION_DSP_IDS_LIST, orgDSPIDs);
          resolve(orgDSPIDs);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetBuyers');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
        true,
      );
    });
  },

  getAllBuyers({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getBuyers(
        (buyers) => {
          commit(types.UPDATE_BUYERS_LIST, buyers);
          resolve(buyers);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetBuyers');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getOrganizationDSPs: createGetAllAction(
    API.instance.getOrganizationDSPs,
    types.UPDATE_ORGANIZATION_DSPS_LIST,
    'messages.failedToGetOrganizationDSPs',
    false,
  ),

  getOrganizationDSPBuyers: createApiAction(
    API.instance.getOrganizationDSPBuyers,
    types.UPDATE_ORGANIZATION_DSP_BUYERS_LIST,
    'messages.failedToGetOrganizationDSPBuyers',
    false,
    (data) =>
      data
        .map((x) => {
          return { ...x, displayName: `${x.name} ${x.dsp_name} ${x.seat_id}` };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  ),

  getOrganizationTopScreenDimensions({ commit }) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getOrganizationTopScreenDimensions(
        (result) => {
          resolve(result);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetOrganizationTopScreenDimensions');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getDashboardAlert({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getDashboardAlert(
        params,
        (alert) => {
          resolve(alert);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetDashboardAlert');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLoginAlert({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getLoginAlert(
        params,
        (alert) => {
          resolve(alert);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLoginAlert');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getMarket({ commit }, { id, includeGeometry = false }) {
    return API.instance.getMarket(id, includeGeometry).catch((error) => {
      const msg = i18n.global.t('messages.failedToGetMarkets');
      ErrorHandler.showError(msg, error);
      throw error;
    });
  },
  getMarkets: createGetAllAction(
    API.instance.getMarkets,
    types.UPDATE_MARKETS_LIST,
    'messages.failedToGetMarkets',
    false,
  ),

  async getMarketsWithInventory(
    { commit },
    {
      ownerIDs,
      countryIDs,
      networkIDs = null,
      unitTargeting = true,
      excludeGeometry = true,
      cancelTokenSource = null,
      showLoader = true,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const params = {
      owner_ids: ownerIDs.join(','),
      country_ids: countryIDs != null ? countryIDs.join(',') : null,
      network_ids: networkIDs ? networkIDs.join(',') : undefined,
      unit_targeting: unitTargeting,
      exclude_geometry: excludeGeometry,
    };
    const markets = await API.instance.getMarketsWithInventory({ params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetMarkets');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.UPDATE_MARKETS_LIST, markets);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return markets;
  },

  getMediaTypes: createGetAllAction(
    API.instance.getMediaTypes,
    types.UPDATE_MEDIA_TYPES_LIST,
    'messages.failedToGetMediaTypes',
    false,
  ),

  getFilterChanges: createApiAction(API.instance.getFilterChanges, null, 'messages.failedToGetFilterChanges', false),

  async getAvailableIDsPerFilterType(
    { dispatch, commit },
    { cancelTokenSource = null, showLoader = false, packageID = null, packageGroupID = null, packageIDs = [] } = {},
  ) {
    const filterChanges = await dispatch('getFilterChanges', {
      data: {
        organization_id: orgState.organization.id,
        package_id: packageID,
        packagegroup_id: packageGroupID,
        package_ids: packageIDs,
        // TODO: Activate this when the filter endpoint accepts unitpacks
        filter_types: ['location_id', 'market_id', 'mediatype_id', 'network_id'] /* , 'unitpack_id' */,
      },
      cancelTokenSource,
      showLoader,
    });

    const filters = {};
    const keys = Object.keys(filterChanges);
    keys.forEach((key) => {
      filters[key] = filterChanges[key].map((x) => parseInt(x, 10));
    });

    return filters;
  },

  async getAllUnitDimensions({ commit }, { cancelTokenSource = null, showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const dimensions = await API.instance.getAllUnitDimensions({ cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToUnitDimensions');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    commit(types.UPDATE_ALL_UNIT_DIMENSIONS, dimensions || []);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return dimensions;
  },

  async getOrganizationSubscriptions({ commit }, { cancelTokenSource = null, showLoader = false } = {}) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const subcriptions = await API.instance.getOrganizationSubscriptions({ cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetSubscriptions');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return subcriptions;
  },

  subscribeToOrganizationSubscriptions({ commit }, subscriptionType) {
    return new Promise((resolve, reject) => {
      API.instance.subscribeToOrganizationSubscriptions(
        subscriptionType,
        () => {
          resolve();
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSubscribeToOrganizationSubscriptions');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },
  deleteOrganizationSubscription({ commit }, subscriptionID) {
    return new Promise((resolve, reject) => {
      API.instance.deleteOrganizationSubscription(
        subscriptionID,
        () => {
          resolve();
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSubscribeToOrganizationSubscriptions');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },
};

export default actions;
